<script setup lang="ts">
  import {
    Modal,
    UiButton,
    DropDown,
    FormInput,
    FormFieldContainer,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { onMounted, type Ref, ref } from 'vue'
  import { useProductStore } from '@/store/product.store.ts'
  import type { ContactPerson, CreateProject } from '@/model/create-project.model.ts'
  import { useLegacyProjectStore } from '@/store/legacy-project.store'
  import type { Project } from '@/types/project.ts'
  import { isAxiosError } from 'axios'
  import LeafletMap from '@/components/LeafletMap.vue'
  import { SCROLL_ZOOM_MODE } from '@/types/scroll-zoom-mode'

  const productStore = useProductStore()
  const projectStore = useLegacyProjectStore()

  const toastStore = useToastStore()

  const data: Ref<CreateProject> = ref({
    projectNaam: '',
    projectNummer: '',
    projectOmschrijving: '',
    projectLocatie: [0, 0],
    materialen: [],
    contactPersoon: {
      naam: '',
      telefoonNummer: '',
      email: '',
    },
  })

  const emit = defineEmits(['close'])

  const errors: Ref<Partial<Record<keyof CreateProject | keyof ContactPerson, string>>> = ref({})
  const isSubmitting: Ref<boolean> = ref(false)

  const selectedLocation: Ref<[number, number] | undefined> = ref()

  async function submit() {
    errors.value = validateInput(data.value)

    if (Object.keys(errors.value).length > 0) {
      toastStore.error('Er zijn één of meerdere velden verkeerd ingevuld.')
      return
    }

    if (!selectedLocation.value) return

    isSubmitting.value = true

    data.value.projectLocatie = selectedLocation.value

    let project: Project
    try {
      project = await projectStore.createProject(data.value)
    } catch (error) {
      if (isAxiosError(error) && error.status !== undefined && error.status < 500) {
        toastStore.error(error.response?.data.message, 3000)
        isSubmitting.value = false
        return
      }
      throw error
    }

    if (project) {
      await projectStore.fetchProjects()
      toastStore.success('Project succesvol aangemaakt')
      emit('close')
    } else {
      isSubmitting.value = false
    }
  }

  function validateInput(input: Partial<CreateProject>) {
    const errors: Partial<Record<keyof CreateProject | keyof ContactPerson, string>> = {}

    if (input.materialen == null || input.materialen.length === 0) {
      errors.materialen = 'Minimaal één product moet worden geselecteerd'
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.contactPersoon?.email ?? '')) {
      errors.email = 'Voer een geldig contact email-address in'
    }

    if (input.contactPersoon?.naam == null || input.contactPersoon.naam.length === 0) {
      errors.naam = 'Contactpersoon is verplicht'
    }

    if (input.projectNaam == null || input.projectNaam.length === 0) {
      errors.projectNaam = 'Project naam is verplicht'
    }

    if (input.projectNummer == null || input.projectNummer.length === 0) {
      errors.projectNummer = 'Projectnummer is verplicht'
    }

    if (input.projectOmschrijving == null || input.projectOmschrijving.length === 0) {
      errors.projectOmschrijving = 'Project omschrijving is verplicht'
    }

    if (!/^[\d\s\-+()]+$/.test(input.contactPersoon?.telefoonNummer ?? '')) {
      errors.telefoonNummer = 'Voer een geldig telefoonnummer in'
    }

    if (selectedLocation.value == null || selectedLocation.value.length !== 2) {
      errors.projectLocatie = 'Selecteer een project locatie op de kaart'
    }

    return errors
  }

  onMounted(async () => {
    await productStore.fetchOrganizationProducts()
  })
</script>

<template>
  <Modal :show="true" @update:show="$emit('close')">
    <template #header>
      <h2 class="header remove-me-header">Project aanmaken</h2>
    </template>

    <FormInput
      label="Project Naam"
      placeholder="Projectnaam"
      v-model="data.projectNaam"
      :error="errors['projectNaam']"
    />
    <FormInput
      label="Projectnummer"
      placeholder="Projectnummer"
      v-model="data.projectNummer"
      :error="errors['projectNummer']"
    />
    <FormInput
      label="Project omscrijving"
      placeholder="Projectomschrijving"
      v-model="data.projectOmschrijving"
      :error="errors['projectOmschrijving']"
    />

    <FormInput
      label="Contact Naam"
      placeholder="Contactnaam"
      v-model="data.contactPersoon.naam"
      :error="errors['naam']"
    />
    <FormInput
      label="Contact E-mail"
      placeholder="E-mailadres"
      v-model="data.contactPersoon.email"
      :error="errors['email']"
    />
    <FormInput
      label="Contact Telefoonnummer"
      placeholder="Telefoonnummer"
      v-model="data.contactPersoon.telefoonNummer"
      :error="errors['telefoonNummer']"
    />

    <DropDown
      label="Producten"
      :required="true"
      :enableSearch="true"
      :allowNew="false"
      idKey="uuid"
      :error="errors['materialen']"
      v-model:multiselectData="data.materialen"
      :data="productStore.organizationProducts"
      :allowMultiselect="true"
    >
      <template #empty>Selecteer product(en)</template>
      <template #multipleItems="{ items }">
        <div class="item-container">
          <span v-for="chosenItem in items" :key="chosenItem.uuid" class="item">{{
            chosenItem.naam ?? chosenItem.omschrijving
          }}</span>
        </div>
      </template>
      <template #item="{ item }">
        <div class="selection-option" :class="data.materialen.includes(item) && 'selected-option'">
          <span class="item-name">
            {{ item.naam ?? item.omschrijving }}
          </span>
          <span class="item-name">
            {{ item.euralCode?.labels['nl'] }}
          </span>
        </div>
      </template>
    </DropDown>

    <FormFieldContainer label="Project Locatie" :error="errors['projectLocatie']">
      <div class="map-container">
        <p>Gebruik de linker muisknop op de kaart om een locatie te selecteren.</p>
        <LeafletMap
          :startingCoordinates="[52.09, 5.12]"
          :startingZoom="10"
          :minZoom="0"
          :maxZoom="18"
          :scrollZoomMode="SCROLL_ZOOM_MODE.ON_SHIFT"
          v-model:marker="selectedLocation"
        />
      </div>
    </FormFieldContainer>

    <template #footer="">
      <UiButton variant="default" label="Sluiten" @click="emit('close')" />
      <UiButton variant="prominent" label="Opslaan" :disabled="isSubmitting" @click="submit" />
    </template>
  </Modal>
</template>

<style scoped>
  .remove-me-header {
    margin: 0;
  }

  .header {
    font-family: Inter;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: 40px;
  }

  .map-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 32rem;
    position: relative;
    z-index: 0;
  }

  .selected-option {
    font-weight: bold;
  }

  .item-container {
    display: flex;
    flex-direction: row;
    justify-content: start;
    gap: var(--spacing-xs);
  }

  .item {
    padding: var(--spacing-xs);
    background-color: var(--colours-grey-3);
    border-radius: var(--components-component-corners);
  }

  .selection-option {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-xs);
  }

  .selection-option .item-name {
    flex: 1;
  }
</style>
